import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {CodeResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CodeFormValue, codeFormSchema} from '../schemas/CodeFormSchema';

export interface CodeFormProps extends RequiredTestIdProps {
  code?: Pick<CodeResponseBody, 'name' | 'localizedNames'>;
  onSubmit: FormSubmitHandler<CodeFormValue>;
}

export function CodeForm(props: CodeFormProps) {
  const {isMultiLocale, supportedLocales} = useTenant();

  const localeFields = isMultiLocale ? supportedLocales : [];

  const defaultValues: CodeFormValue = {
    name: props.code?.name ?? '',
    localizedNames: localeFields.map((locale) => ({
      locale: locale.value,
      name: props.code?.localizedNames?.find((entry) => entry.locale === locale.value)?.name ?? '',
    })),
  };

  return (
    <Form<CodeFormValue>
      onSubmit={props.onSubmit}
      experimentalZodSchema={codeFormSchema}
      defaultValues={defaultValues}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            label={i18n.t('page.generalSettings.labels.defaultValue')}
            control={control}
            name="name"
            type="text"
            isRequired
            isAutoFocused
            data-testid={suffixTestId('code-name', props)}
          />
          <Show when={localeFields.length > 0}>
            <VStack spacing={4}>
              {localeFields.map((locale, index) => (
                <FormField
                  key={locale.value}
                  label={locale.label}
                  control={control}
                  name={`localizedNames.${index}.name`}
                  type="text"
                  placeholder={i18n.t('entity.codelist.labels.localizedNamePlaceholder')}
                  data-testid={suffixTestId(`localized-name-${locale.value}`, props)}
                />
              ))}
            </VStack>
          </Show>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              onClick={closeCurrentDialog}
              title={i18n.t('general.actions.cancel')}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('save', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
